import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BusinessTabs from '../../molecules/Desktop/BusinessTabs';
import { BusinessStageType, BusinessTabType, MasterMenuItem, MenuSource, MyBusinessSchema } from '../../data/types';
import { useCompleteSignIn } from '../../hooks/authhooks';
import {
  useBusinessAssociatedResources,
  useBusinessDetails,
  useComplianceSessionsForUser,
  useUpdateBusinessDetails,
  useUpdateBusinessMenu,
  useUpdateBusinessShare,
} from '../../hooks/businesshooks';
import { formatBusinessLocation } from '../../utils/businessProfile';
import { SHARE_CONSENT_VERSION } from '../../utils/fundingPartners';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import IntegrationLogo from '../../components/IntegrationLogo';
import { getAllResources } from '../../api/crud';
import { dscvryExpertPath, isDscvryResource } from '../../utils/contactBuilder';
import { ReactComponent as MyBusinessIcon } from '../../icons/MyBusinessIcon.svg';

const POS_STUBS: Array<{ source: MenuSource; label: string }> = [
  { source: 'square', label: 'Square' },
  { source: 'clover', label: 'Clover' },
  { source: 'doordash', label: 'DoorDash' },
  { source: 'uber_eats', label: 'Uber Eats' },
  { source: 'grubhub', label: 'Grubhub' },
];

const INTEGRATIONS = [
  { id: 'dscvry', label: 'DSCVRY', description: 'Build a verified contact list for this business. Setup stays in DSCVRY.' },
  { id: 'airiwheels', label: 'AiriWheels', description: 'Share selected operating data with your financial operations partner.' },
  { id: 'square', label: 'Square', description: 'Import menu and point-of-sale data.' },
  { id: 'clover', label: 'Clover', description: 'Import menu and point-of-sale data.' },
  { id: 'doordash', label: 'DoorDash', description: 'Bring delivery menu data into your master menu.' },
  { id: 'uber_eats', label: 'Uber Eats', description: 'Bring delivery menu data into your master menu.' },
  { id: 'grubhub', label: 'Grubhub', description: 'Bring delivery menu data into your master menu.' },
] as const;

type HubTab = 'compliance' | 'menu' | 'integrations';

const BusinessHub: React.FC = () => {
  const { businessId = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useCompleteSignIn();
  const uid = user?.uid || '';
  const { businessDetails, reloadBusinessDetails, isLoadingBusinessDetails } = useBusinessDetails(uid, businessId);
  const { resources = [] } = useBusinessAssociatedResources(uid, businessId);
  const { sessions = [], reloadComplianceSessions } = useComplianceSessionsForUser(uid);
  const { createBusinessReadiness, isGeneratingBusinessReadiness } = useUpdateBusinessDetails(uid);
  const { updateMenu } = useUpdateBusinessMenu(uid, businessId);
  const { updateShare } = useUpdateBusinessShare(uid, businessId);
  const business = useMemo(() => (businessDetails || {}) as MyBusinessSchema, [businessDetails]);
  const [hubTab, setHubTab] = useState<HubTab>('compliance');
  const [dishName, setDishName] = useState('');
  const [ingredientName, setIngredientName] = useState('');
  const [ingredientQty, setIngredientQty] = useState('1');
  const [shareMenu, setShareMenu] = useState(false);
  const [shareSupplies, setShareSupplies] = useState(false);
  const [shareSales, setShareSales] = useState(false);
  const [shareSaved, setShareSaved] = useState('');
  const [readinessError, setReadinessError] = useState('');
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setShareMenu(Boolean(business.partner_share?.menu));
    setShareSupplies(Boolean(business.partner_share?.supplies));
    setShareSales(Boolean(business.partner_share?.sales));
  }, [business.partner_share?.menu, business.partner_share?.sales, business.partner_share?.supplies]);

  const menuItems = business.master_menu?.items || [];
  const sources = business.master_menu?.sources || POS_STUBS.map((item) => ({ ...item, connection_status: 'stub' as const }));
  const linkedSessions = sessions.filter((session) => session.business_id === businessId);
  const importedSessions = linkedSessions.filter((session) => session.source !== 'business_readiness');
  const legacySessions = sessions.filter((session) => !session.business_id);
  const isMobile = width < 1024;
  const startTab = { dream: 'business', build: 'recommendations', scale: 'menu' } as BusinessTabType;
  const [businessStage, setBusinessStage] = useState<BusinessStageType>(business.business_stage || 'dream');

  useEffect(() => {
    if (business.business_stage) setBusinessStage(business.business_stage);
  }, [business.business_stage]);

  const handleRefreshReadiness = async () => {
    setReadinessError('');
    try {
      await createBusinessReadiness(businessId, true);
      await reloadBusinessDetails();
      await reloadComplianceSessions();
    } catch {
      setReadinessError('Could not refresh Compliance Readiness. Please try again.');
    }
  };

  const persistMenu = async (items: MasterMenuItem[], nextSources = sources) => {
    await updateMenu(items, nextSources);
  };

  const handleAddDish = async () => {
    const name = dishName.trim();
    if (!name) return;
    const existing = menuItems.find((item) => item.name.trim().toLowerCase() === name.toLowerCase());
    const recipe_lines = ingredientName.trim()
      ? [{ item_name: ingredientName.trim(), quantity: Number(ingredientQty) || 1, measure_unit: 'oz' as const }]
      : [];
    if (existing) {
      const merged = menuItems.map((item) =>
        item === existing
          ? { ...item, recipe_lines: [...(item.recipe_lines || []), ...recipe_lines] }
          : item
      );
      await persistMenu(merged);
    } else {
      await persistMenu([...menuItems, { name, source: 'manual', recipe_lines }]);
    }
    setDishName('');
    setIngredientName('');
  };

  const handleCsvImport = async (file: File) => {
    const text = await file.text();
    const rows = text.split(/\r?\n/).map((line) => line.split(',')[0]?.trim()).filter(Boolean);
    const names = new Map(menuItems.map((item) => [item.name.trim().toLowerCase(), item]));
    rows.forEach((name) => {
      const key = name.toLowerCase();
      if (!names.has(key)) names.set(key, { name, source: 'csv' });
    });
    await persistMenu(Array.from(names.values()), [
      ...sources.filter((item) => item.source !== 'csv'),
      { source: 'csv', connection_status: 'connected', label: 'CSV import' },
    ]);
  };

  const handleSaveShare = async () => {
    await updateShare({
      partner_slug: 'airiwheels',
      menu: shareMenu,
      supplies: shareSupplies,
      sales: shareSales,
      consent_version: SHARE_CONSENT_VERSION,
      granted_at: (shareMenu || shareSupplies || shareSales) ? new Date().toISOString() : '',
    });
    setShareSaved('Share preferences saved. Nothing extra was sent on your original check-fit referral.');
  };

  if (isLoadingBusinessDetails && !business.business_name) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primaryDark-400"></div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col min-h-screen bg-white ${isMobile ? 'pb-16' : ''}`} data-testid="business-hub">
      <BusinessTabs startTab={startTab} businessStage={businessStage} setBusinessStage={setBusinessStage} />
      <div className={`flex-1 ${isMobile ? 'px-4 py-2' : 'px-6 py-4'}`}>
        <button type="button" className="text-md mx-10 border-0 font-semibold text-gray-400 flex items-center gap-2" onClick={() => navigate('/business')}>
          <ArrowBackIosIcon sx={{ fontSize: 14 }} />
          <span className="text-md font-raleway font-semibold">All Businesses</span>
        </button>
        <div className={`max-w-7xl mx-auto w-full ${isMobile ? 'py-2' : 'py-4'}`}>
          
          <div className="flex items-center gap-3 mb-1">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-[#EFF8FA] flex items-center justify-center border border-gray-100 shrink-0">
              {business.logo ? (
                <img src={business.logo} alt="" className="w-full h-full object-contain bg-white" />
              ) : (
                <MyBusinessIcon className="w-6 h-6 text-primaryDark-300" />
              )}
            </div>
            <h1 className="text-2xl font-raleway font-semibold text-ink">{business.business_name || 'My Business'}</h1>
          </div>
          <p className="text-[#3F5B64] font-open-sans mb-6">{formatBusinessLocation(business)}</p>
          <div className="flex gap-4 border-b border-[#E4EEF1] mb-6">
            {(['compliance', 'menu', 'integrations'] as HubTab[]).map((tab) => (
              <button
                key={tab}
                type="button"
                className={`pb-2 rounded-md border border-b-1 mb-0 rounded-b-none hover:bg-primary-tint border-[#E4EEF1] px-4 py-2 text-sm font-raleway ${hubTab === tab ? 'font-bold text-primaryDark-100' : 'text-gray-500'}`}
                onClick={() => setHubTab(tab)}
              >
                {tab === 'compliance' ? 'Compliance' : tab === 'menu' ? 'Master menu' : 'Integrations'}
              </button>
            ))}
          </div>

          {hubTab === 'compliance' && (
            <section>
              <p className="text-sm text-[#3F5B64] mb-4">
                Generated from this business profile and its location. Imported checklists remain available below.
              </p>
              {business.business_readiness?.compliance?.items?.length ? (
                <div className="mb-5 rounded-xl border border-[#D5E3E8] bg-[#F7FBFC] p-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h2 className="font-raleway text-lg font-bold text-[#0A3751]">Current Compliance Readiness</h2>
                      <p className="text-xs text-[#3F5B64]">
                        {business.business_readiness.compliance.items.length} items · {business.business_readiness.compliance.source || 'business profile'}
                      </p>
                    </div>
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#0A3751]">
                      {business.business_readiness.stage.label}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {business.business_readiness.compliance.items.map((item) => (
                      <li key={item.item_key} className="rounded-lg border border-[#E4EEF1] bg-white p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-ink">{item.title}</p>
                            {item.description && <p className="mt-1 text-sm text-[#3F5B64]">{item.description}</p>}
                            {item.legal?.agency && <p className="mt-1 text-xs text-[#607982]">Agency: {item.legal.agency}</p>}
                          </div>
                          <span className="rounded-full bg-primary-tint px-2 py-1 text-[11px] font-semibold text-primaryDark-100">
                            {item.priority || 'required'}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {business.business_readiness.compliance.region_note && (
                    <p className="mt-3 text-xs text-[#607982]">{business.business_readiness.compliance.region_note}</p>
                  )}
                </div>
              ) : (
                <p className="mb-4 rounded-lg bg-[#F7FBFC] p-4 text-sm text-[#3F5B64]">
                  No generated checklist is stored for this business yet.
                </p>
              )}
              {importedSessions.length > 0 && (
                <div className="mb-5">
                  <h3 className="mb-2 font-semibold">Imported checklists</h3>
                  {importedSessions.map((session) => (
                  <div key={String(session.id)} className="rounded-xl border border-[#E4EEF1] p-4 mb-3">
                    <p className="font-semibold">{String(session.persona || 'Checklist')}</p>
                    <p className="text-sm text-[#3F5B64]">Source: {String(session.source || 'api')} · {Array.isArray(session.items) ? session.items.length : 0} items</p>
                  </div>
                  ))}
                </div>
              )}
              {legacySessions.length > 0 && (
                <details className="mb-5 rounded-xl border border-[#E4EEF1] p-4">
                  <summary className="cursor-pointer font-semibold">Unlinked legacy checklists ({legacySessions.length})</summary>
                  <p className="mt-2 text-xs text-[#607982]">These checklists belong to your account but predate business linking.</p>
                  {legacySessions.map((session) => (
                    <div key={String(session.id)} className="mt-3 rounded-lg bg-[#F7FBFC] p-3">
                      <p className="font-semibold">{String(session.business_name || session.persona || 'Checklist')}</p>
                      <p className="text-sm text-[#3F5B64]">{Array.isArray(session.items) ? session.items.length : 0} items</p>
                    </div>
                  ))}
                </details>
              )}
              <button
                type="button"
                className="inline-flex h-11 items-center rounded-lg bg-primaryDark-0 px-4 font-semibold text-white"
                disabled={isGeneratingBusinessReadiness}
                onClick={() => void handleRefreshReadiness()}
              >
                {isGeneratingBusinessReadiness ? 'Refreshing…' : business.business_readiness ? 'Refresh from business details' : 'Generate from business details'}
              </button>
              {readinessError && <p className="mt-2 text-sm text-red-600">{readinessError}</p>}
              <div className="mt-6">
                <h3 className="font-semibold mb-2">Linked resources</h3>
                {resources.length === 0 ? (
                  <p className="text-sm text-[#3F5B64]">No linked pantry resources yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {resources.map((resource) => (
                      <li key={resource.id}>
                        <Link className="text-primaryDark-0 font-semibold" to={`/${resource.resource_type || 'funding'}/${resource.id}`}>
                          {resource.name || resource.id}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}

          {hubTab === 'menu' && (
            <section>
              <p className="text-sm text-[#3F5B64] mb-4">
                Craft dishes on one master menu with manual entry or CSV import.
              </p>
              <div className="grid gap-3 sm:grid-cols-2 mb-4">
                <input className="border border-[#E4EEF1] rounded-lg p-3" placeholder="Dish name" value={dishName} onChange={(e) => setDishName(e.target.value)} />
                <input className="border border-[#E4EEF1] rounded-lg p-3" placeholder="Ingredient" value={ingredientName} onChange={(e) => setIngredientName(e.target.value)} />
                <input className="border border-[#E4EEF1] rounded-lg p-3" placeholder="Qty" value={ingredientQty} onChange={(e) => setIngredientQty(e.target.value)} />
                <button type="button" className="rounded-lg bg-primaryDark-0 text-white font-semibold" onClick={() => void handleAddDish()}>
                  Add to master menu
                </button>
              </div>
              <label className="text-sm font-semibold block mb-4">
                Import CSV (first column = dish name)
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="block mt-1"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleCsvImport(file);
                  }}
                />
              </label>
              <ul className="space-y-3">
                {menuItems.map((item) => (
                  <li key={item.name} className="rounded-xl border border-[#E4EEF1] p-4">
                    <p className="font-semibold">{item.name}</p>
                    <p className="text-xs text-[#3F5B64]">Source: {item.source || 'manual'}</p>
                    {(item.recipe_lines || []).map((line) => (
                      <p key={line.item_name} className="text-sm text-[#3F5B64]">
                        {line.item_name} · {line.quantity} {line.measure_unit || ''}
                        {line.cost?.unit_cost != null ? ` · $${line.cost.unit_cost}` : ''}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {hubTab === 'integrations' && (
            <section>
              <div className="mb-5">
                <h2 className="font-raleway text-xl font-bold text-ink">Connect your business apps</h2>
                <p className="mt-1 text-sm text-[#3F5B64]">
                  DSCVRY builds a contact list for this business. Venture setup stays in DSCVRY. AiriWheels sharing is optional and separate from check-fit. POS and delivery connections are preview-only in this pilot.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {INTEGRATIONS.map((integration) => {
                  const source = sources.find((item) => item.source === integration.id);
                  const airiConnected = shareMenu || shareSupplies || shareSales;
                  const connected = integration.id === 'airiwheels'
                    ? airiConnected
                    : source?.connection_status === 'connected';
                  return (
                    <article key={integration.id} className="rounded-2xl border border-[#D5E3E8] bg-white p-5 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F2F8FA]">
                            <IntegrationLogo integrationId={integration.id} />
                          </div>
                          <div>
                            <h3 className="font-raleway font-bold text-ink">{integration.label}</h3>
                            <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                              connected ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {connected ? 'Connected' : integration.id === 'airiwheels' ? 'Not sharing' : integration.id === 'dscvry' ? 'Setup in DSCVRY' : 'Coming soon'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-[#3F5B64]">{integration.description}</p>
                      {integration.id === 'dscvry' ? (
                        <button
                          type="button"
                          className="mt-4 h-10 rounded-lg bg-primaryDark-0 px-4 text-sm font-semibold text-white"
                          onClick={() => {
                            const linked = (resources || []).find((item) => isDscvryResource(item));
                            if (linked?.id) {
                              navigate(`/expert/${linked.id}?business=${businessId}`);
                              return;
                            }
                            void getAllResources('expert').then((experts) => {
                              navigate(dscvryExpertPath(experts || [], businessId));
                            });
                          }}
                        >
                          Build contact list
                        </button>
                      ) : integration.id === 'airiwheels' ? (
                        <div className="mt-4 border-t border-[#E4EEF1] pt-4">
                          <label className="mb-2 flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={shareMenu} onChange={(e) => setShareMenu(e.target.checked)} /> Master menu
                          </label>
                          <label className="mb-2 flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={shareSupplies} onChange={(e) => setShareSupplies(e.target.checked)} /> Supplies / ingredient costs
                          </label>
                          <label className="mb-4 flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={shareSales} onChange={(e) => setShareSales(e.target.checked)} /> Sales transactions
                          </label>
                          <button type="button" className="h-10 rounded-lg bg-primaryDark-0 px-4 text-sm font-semibold text-white" onClick={() => void handleSaveShare()}>
                            Save sharing
                          </button>
                          {shareSaved && <p className="mt-3 text-xs text-[#3F5B64]">{shareSaved}</p>}
                        </div>
                      ) : (
                        <button type="button" disabled className="mt-4 h-10 rounded-lg border border-[#D5E3E8] px-4 text-sm font-semibold text-gray-400">
                          Coming soon
                        </button>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessHub;
